<script setup lang="ts">
import { computed, reactive, ref, watchEffect } from "vue";
import { locale } from "../locale";
import LoadingIcon from "../components/chat/LoadingIcon.vue";
import { router } from "../router";
import { ws_instance } from "../websocket/ws-instance";
import { fetcher } from "../api/fetcher";
import { formattedDate } from "../utils/date";
import SimpleBar from "simplebar";
import { cleanup } from "../states/media/state";

const loading = ref(true);
ws_instance.disconnect();
cleanup()
const props = defineProps<{
  summaryId: string;
}>();
const errorReason = ref<string | null>(null);
interface SummaryData {
  text: string;
  date: string;
}
interface SummaryErr {
  reason: string;
}
const data = reactive({
  text: "",
  date: "",
});
function getSummaryData() {
  fetcher
    .get({
      path: `/records/summary/data/${props.summaryId}`,
    })
    .then((res: SummaryData) => {
      data.text = res.text;
      data.date = formattedDate(res.date);
      loading.value = false;
    })
    .catch((err: { data: SummaryErr }) => {
      if (err.data.reason === "not ready") {
        setTimeout(getSummaryData, 5000);
      } else {
        errorReason.value = locale.summaryPage.notFound;
      }
    });
}
getSummaryData();
const isBack = computed(() => {
  return !!router.options.history.state.back;
});
const scroll = ref<HTMLElement>();
watchEffect(() => {
  if (scroll.value) {
    new SimpleBar(scroll.value, { autoHide: false });
  }
});
const downloadSummary = () => {
  const a = document.createElement("a");
  a.href = window.location.origin + `/api/records/summary/${props.summaryId}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
</script>
<template>
  <div class="ai-summary-wrapper">
    <div class="ai-summary-block">
      <div v-if="loading || !!errorReason" class="loading-summary-block">
        <div v-if="!errorReason" class="inner">
          <span>{{ locale.summaryPage.loading }}</span>
          <div class="loading">
            <LoadingIcon />
          </div>
        </div>
        <div v-else class="inner">
          <span>{{ errorReason }}</span>
        </div>
      </div>
      <div class="title">
        <span class="text"
          >{{ data.date }} - {{ locale.summaryPage.title }}</span
        >
        <div v-if="isBack" class="back" @click="router.back()">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20 12L4 12M4 12L10.5 5.5M4 12L10.5 18.5"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <div class="download-summary" @click="downloadSummary">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12.0005 3.60005V17.2M12.0005 17.2L17.1002 12.2891M12.0005 17.2L6.90083 12.2891M4.80054 20.4001H19.2005"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </div>
      <div class="room-chat-block">
        <div class="chat-area">
          <div ref="scroll" class="content">
            <div class="content-inner">
              <span>{{ data.text }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
.ai-summary-wrapper {
  width: 100vw;
  height: 100%;
  height: 100dvh;
  background-color: #1e1818;
  background-image: var(--bg-image);
  background-repeat: repeat;
  display: flex;
  justify-content: center;
  padding: 12px;
}

.ai-summary-block {
  width: 720px;
  max-width: 100%;
  height: 100%;
  background-color: #1e1818;
  border: 1px solid #494949;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  border-radius: 8px;
}

.title {
  height: 40px;
  width: 100%;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #332f2f;
  color: #f6f6f6;
  flex-shrink: 0;
}

.title .text {
  font: Open Sans;
  font-size: 14px;
  font-weight: 600;
}

.room-chat-block {
  height: 100%;
  width: 100%;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.chat-area {
  width: 100%;
  height: 100px;
  flex-grow: 1;
  position: relative;
  background-image: url("@images/chat_bg3.png");
  background-repeat: repeat;
}

.content {
  height: 100%;
}

.content-inner {
  padding: 12px 16px;
  white-space: pre-wrap;
  font-family: Open Sans;
  font-size: 14px;
  font-weight: 400;
  text-align: left;
  color: #ffffff;
}

.loading-summary-block {
  position: absolute;
  z-index: 100;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 8px;
  background-color: rgba(30, 24, 24, 0.9);
  backdrop-filter: blur(4px);
}

.loading-summary-block .inner {
  border-radius: 8px;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 14px;
}

.reload {
  cursor: pointer;
}

.back {
  position: absolute;
  top: 8px;
  left: 8px;
  cursor: pointer;
  color: #a4a4a4;
}

.back:hover {
  color: #d8d8d8;
}

.download-summary {
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;
  color: #a4a4a4;
}

.download-summary:hover {
  color: #d8d8d8;
}
</style>
<style>
.ai-summary-wrapper .simplebar-vertical .simplebar-scrollbar::before {
  background: rgba(116, 116, 116, 0.85);
  width: 4px;
}
.ai-summary-wrapper .simplebar-vertical {
  top: 4px !important;
  bottom: 4px !important;
}
</style>
